import './DocumentGen.css';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm'
import html2pdf from 'html2pdf.js';
import { useEffect, useState } from 'react';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { auth, db } from '../Firebase_Config/firebase';
import Snackbar from '@mui/material/Snackbar';
import MuiAlert from '@mui/material/Alert';

const DROPDOWNS = [
  {
    key: 'aiSystem',
    title: 'AI System Technical Documentation',
    description:
      'Detailed records demonstrating compliance with the Act’s requirements, including system architecture, intended use, training data sources, model versions, and performance metrics.',
    prompt:
      'Generate AI System Technical documentation in line with the EU AI Act taking all of the following information into account as background to make it relevant to the organisation\'s specific needs - the information is: {answers}.',
    fields: [
      'What is the specific intended purpose and function of your AI system?',
      'Who is the provider (organisation name, contact details)?',
      'What are the system’s version, deployment context (e.g., API, embedded), and hardware/software requirements?',
      'How does your system interact with other systems or products?',
      'What development processes, methodologies, and tools were used?',
      'What data sources were used for training, validation, and testing? How was data quality ensured?',
      'What are the system’s performance metrics, accuracy levels, and known limitations for your use case?',
      'What security, robustness, and testing measures have you implemented?',
      'What changes or updates have been made to the system throughout its lifecycle?',
    ],
  },
  {
    key: 'riskManage',
    title: 'Risk Management Policy',
    description: 'Procedures for ongoing risk assessment, mitigation, and monitoring of AI system risks throughout the lifecycle.',
    prompt: 'Generate a Risk Management Policy in line with the EU AI Act taking all of the following information into account as background to make it relevant to the organisation\'s specific needs - the information is: {answers}.',
    fields: [
        'What are the specific risks associated with your AI system in its intended application?',
        'What procedures do you have for identifying, assessing, and mitigating these risks?',
        'How often are risks reviewed, and who is responsible for this process?',
        'How do you document and respond to incidents or near-misses?',
    ],
  },
  {
    key: 'dataGov',
    title: 'Data Governance Policy',
    description: 'Documentation of data sourcing, quality assurance, data preprocessing, and measures to ensure data integrity and representativeness.',
    prompt: 'Generate a Data Governance Policy in line with the EU AI Act taking all of the following information into account as background to make it relevant to the organisation\'s specific needs - the information is: {answers}.',
    fields: [
        'What are your data sourcing practices, and how do you ensure data is representative and free from bias?',
        'What steps do you take for data preprocessing, cleaning, and validation?',
        'How do you manage access, storage, and retention of data?',
        'What measures are in place to protect data privacy and comply with GDPR?',
    ],
  },
  {
    key: 'impactRep',
    title: 'AI Impact Assessment Reports',
    description: 'Structured assessments identifying potential harms, unintended consequences, human oversight mechanisms, and fallback procedures for high-risk systems.',
    prompt: 'Generate a AI Impact Assessment Report in line with the EU AI Act taking all of the following information into account as background to make it relevant to the organisation\'s specific needs - the information is: {answers}.',
    fields: [
        'What potential impacts (positive and negative) could your AI system have on individuals, groups, and society?',
        'What steps have you taken to identify and mitigate unintended consequences?',
        'What human oversight mechanisms are in place?',
        'How do you ensure fundamental rights and non-discrimination?',
    ],
  },
  {
    key: 'humanProce',
    title: 'Human Oversight Procedures',
    description: 'Clear protocols for human-in-the-loop validation, escalation paths for appeals, and explanation interfaces to ensure meaningful human control over AI decisions.',
    prompt: 'Generate a Human Oversight Procedures document in line with the EU AI Act taking all of the following information into account as background to make it relevant to the organisation\'s specific needs - the information is: {answers}.',
    fields: [
        'Who is responsible for human oversight of the AI system?',
        'What are the escalation and intervention protocols if the AI system malfunctions or produces questionable outputs?',
        'How are users trained to interpret and act on AI system outputs?',
    ],
  },
  {
    key: 'userInfoPol',
    title: 'Transparency and User Information Policy',
    description: 'Guidelines for informing users about AI system use, capabilities, and limitations, especially for high-risk or limited-risk systems.',
    prompt: 'Generate a Transparency and User Information Policy in line with the EU AI Act taking all of the following information into account as background to make it relevant to the organisation\'s specific needs - the information is: {answers}.',
    fields: [
        'How do you inform users about the presence and purpose of AI in your products/services?',
        'What information do you provide about system capabilities, limitations, and decision-making processes?',
        'How is this information communicated (e.g., user manuals, online help)?',
    ],
  },
  {
    key: 'recordPol',
    title: 'Record-Keeping Policy',
    description: 'Systems for logging AI system operations, decisions, and incidents to ensure traceability and accountability.',
    prompt: 'Generate a Record-Keeping Policy in line with the EU AI Act taking all of the following information into account as background to make it relevant to the organisation\'s specific needs - the information is: {answers}.',
    fields: [
        'What logs and records do you keep of system operations, decisions, and incidents?',
        'How long are records retained, and how is access controlled?',
        'How do you ensure traceability and auditability of AI decisions?',
    ],
  },
  {
    key: 'complianceCheck',
    title: 'Conformity Assessment and Compliance Checklists',
    description: 'Documentation of internal and external conformity assessments and compliance checks, including references to relevant standards and frameworks.',
    prompt: 'Generate a Conformity Assessment and Compliance Checklist document in line with the EU AI Act taking all of the following information into account as background to make it relevant to the organisation\'s specific needs - the information is: {answers}.',
    fields: [
        'What internal and external conformity assessments have you conducted?',
        'What standards (e.g., ISO 42001) and technical specifications do you follow?',
        'How do you document compliance with each relevant requirement of the EU AI Act?',
    ],
  },
  {
    key: 'correctProce',
    title: 'Incident Reporting and Correction Procedures',
    description: 'Processes for reporting, documenting, and rectifying system failures, malfunctions, or breaches of compliance.',
    prompt: 'Generate a Incident Reporting and Correction Procedures document in line with the EU AI Act taking all of the following information into account as background to make it relevant to the organisation\'s specific needs - the information is: {answers}.',
    fields: [
        'What is your process for reporting, documenting, and correcting system failures or compliance breaches?',
        'Who is responsible for managing incidents, and how are corrective actions tracked?',
    ],
  },
  {
    key: 'trainPol',
    title: 'Training and Awareness Policy',
    description: 'Records of staff training on AI governance, compliance obligations, and risk management.',
    prompt: 'Generate a Training and Awareness Policy in line with the EU AI Act taking all of the following information into account as background to make it relevant to the organisation\'s specific needs - the information is: {answers}.',
    fields: [
        'What training do staff receive on AI governance, compliance, and risk management?',
        'How is training effectiveness evaluated and updated?',
    ],
  },
  {
    key: 'supplyChain',
    title: 'Partnership and Supply Chain Due Diligence',
    description: 'Procedures for vetting and monitoring third-party providers, suppliers, and partners involved in the AI system lifecycle',
    prompt: 'Generate a Partnership and Supply Chain Due Diligence document in line with the EU AI Act taking all of the following information into account as background to make it relevant to the organisation\'s specific needs - the information is: {answers}.',
    fields: [
        'What criteria do you use to select and monitor third-party providers and partners?',
        'How do you ensure their compliance with the EU AI Act and your internal policies?'
    ],
  },
];


const Dropdown = ({ title, children, disabled = false }) => {
  const [open, setOpen] = useState(false);

  const handleToggle = () => {
    if (disabled) return; // 🚫 block toggle if disabled
    setOpen(!open);
  };

  return (
    <div className={`dropdown ${disabled ? 'disabled' : ''}`}>
      <div
        className="dropdown-header"
        onClick={handleToggle}
        style={{ cursor: disabled ? 'not-allowed' : 'pointer', opacity: disabled ? 0.6 : 1 }}
      >
        <span>{title}</span>
        <span className={`dash-arrow ${open ? 'open' : ''}`}>&#9662;</span>
      </div>
      <div className={`dropdown-content ${open ? 'open' : ''}`}>
        {!disabled && children}
        {disabled && <div className="locked-message">🔒 Upgrade to unlock</div>}
      </div>
    </div>
  );
};

const GET_EMAIL = () => {
  const [formData, setFormData] = useState({ name: '', email: '', message: '' });

  const [toastOpen, setToastOpen] = useState(false);
  const [toastSeverity, setToastSeverity] = useState('success');
  const [toastMessage, setToastMessage] = useState('');

  useEffect(() => {
    const fetchUserData = async () => {
      const user = auth.currentUser;
      if (!user) return;

      const userDocQuery = query(collection(db, 'users'), where('userId', '==', user.uid));
      const userDocSnapshot = await getDocs(userDocQuery);

      if (!userDocSnapshot.empty) {
        const userData = userDocSnapshot.docs[0].data();
        setFormData(prev => ({
          ...prev,
          name: userData.Name || '',
          email: userData.email || ''
        }));
      }
    };

    fetchUserData();
  }, []);

  const handleChange = (e) => {
    setFormData({ ...formData, message: e.target.value });
  };

  const showToast = (message, severity = 'success') => {
    setToastMessage(message);
    setToastSeverity(severity);
    setToastOpen(true);
  };

  const handleCloseToast = (_, reason) => {
    if (reason === 'clickaway') return;
    setToastOpen(false);
  };

  const onSubmit = async (event) => {
    event.preventDefault();

    const formDataToSend = {
      ...formData,
      access_key: "eeb7e415-06eb-495c-a9f8-075545b91e36"
    };

    const res = await fetch("https://api.web3forms.com/submit", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json"
      },
      body: JSON.stringify(formDataToSend)
    }).then((res) => res.json());

    if (res.success) {
      showToast("Email submitted successfully!", "success");
      setFormData(prev => ({ ...prev, message: '' }));
    } else {
      showToast("Failed to submit. Please try again.", "error");
    }
  };

  return (
    <div className="ContactContainer">
      <h1 className='helptitle'>Contact Form</h1>
      <br/>
      <div className='Hom-card-accent'/>
      
      <form onSubmit={onSubmit}>
        <textarea
          className='OtherInputs'
          name="message"
          placeholder="Write Your Message Here.."
          value={formData.message}
          onChange={handleChange}
        />
        <button className='SubmitInput' type="submit">Submit Form</button>
      </form>

      <Snackbar open={toastOpen} autoHideDuration={4000} onClose={handleCloseToast} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
        <MuiAlert onClose={handleCloseToast} severity={toastSeverity} sx={{ width: '100%' }} elevation={6} variant="filled">
          {toastMessage}
        </MuiAlert>
      </Snackbar>
    </div>
  );
};

const DocumentGen = () => {
  const [answers, setAnswers] = useState({});
  const [responseBySection, setResponseBySection] = useState({});
  const [hasPaidTrue, setHasPaidTrue] = useState(null);
  const [showContactModal, setShowContactModal] = useState(false);
  const [showModal, setShowModal] = useState(true);

  const getUserId = () => {
      const user = auth.currentUser;
      return user?.uid;
    };

  const fetchHasPaidTrue = async () => {
      try {
        const userId = getUserId();
        if (!userId) return;
  
        const userDocQuery = query(collection(db, 'users'), where('userId', '==', userId));
        const userDocSnapshot = await getDocs(userDocQuery);
  
        if (!userDocSnapshot.empty) {
          const userDocData = userDocSnapshot.docs[0].data();
          const getHasPaid = userDocData.HasPaid || false;
          setHasPaidTrue(getHasPaid);
        } else {
          console.log('User document not found');
          setHasPaidTrue(false); // default to false if not found
        }
      } catch (error) {
        console.error('Error fetching payment status:', error);
        setHasPaidTrue(false);
      }
    };
  
    useEffect(() => {
      fetchHasPaidTrue();
    });


  const handleChange = (sectionKey, question, value) => {
    setAnswers(prev => ({
      ...prev,
      [sectionKey]: {
        ...prev[sectionKey],
        [question]: value,
      },
    }));
  };

  const buildPrompt = (dropdown) => {
    const sectionAnswers = answers[dropdown.key] || {};
    const answerBlock = dropdown.fields
      .map(q => `${q} - ${sectionAnswers[q] || ''}`)
      .join('. ');
    return dropdown.prompt.replace('{answers}', answerBlock);
  };

  const handleSubmit = async (dropdown) => {
    const finalPrompt = buildPrompt(dropdown);
    const sectionKey = dropdown.key;
    console.log(`Final Prompt- ${finalPrompt}`)
    setResponseBySection(prev => ({
      ...prev,
      [sectionKey]: { loading: true, message: null },
    }));

    const options = {
      method: 'POST',
      headers: {
        Authorization: 'Bearer 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', // Add your API key here
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        model: 'sonar',
        messages: [
          { role: 'system', content: 'Provide precise and concise content suitable for a formal document. Include only relevant information with clear headers and complete sentences. Do not include introductions, conclusions. Only use full sentences. ' },
          { role: 'user', content: finalPrompt },
        ],
        web_search_options: {
          search_context_size: 'high',
        },
      }),
    }

    try {
        const res = await fetch('https://api.perplexity.ai/chat/completions', options);
        const data = await res.json();
        const rawMessage = data?.choices?.[0]?.message?.content || 'No response received.';

        // Extract only numbered sections 1 to 4
        const sectionRegex = /(^[1-4]\..*?)(?=^---|^[1-4]\.|$)/gms;
        const sections = rawMessage.match(sectionRegex);
        const content = sections ? sections.join('\n\n') : rawMessage;

        // Remove all square brackets with only numbers inside (e.g. [1], [23])
        let cleaned = content.replace(/\[\d+\]/g, '');

        // Convert first line to h2 markdown by prepending '## ' to it
        // Split by line breaks:
        const lines = cleaned.split('\n').filter(line => line.trim() !== '');

        if (lines.length > 0) {
            // Replace first line with ## + line (h2 markdown)
            lines[0] = `## ${lines[0].trim()}`;
        }

        cleaned = lines.join('\n');
        console.log(data)
        setResponseBySection(prev => ({
            ...prev,
            [sectionKey]: {
            loading: false,
            message: cleaned,
            citations: data?.citations || [],
            searchResults: data?.search_results || [],
            },
        }));
        } catch (err) {
        console.error(err);
        setResponseBySection(prev => ({
            ...prev,
            [sectionKey]: { loading: false, message: 'An error occurred.', citations: [], searchResults: [] },
        }));
    }

  };

    const handleSavePDF = (key, title) => {
        const element = document.getElementById(`pdf-content-${key}`);
        // Replace spaces with underscores for filename
        const filename = title.replace(/\s+/g, '_') + '.pdf';

        const opt = {
            margin: 10,
            filename: filename,
            image: { type: 'jpeg', quality: 0.98 },
            html2canvas: { scale: 2, useCORS: true },
            jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' },
            pagebreak: { mode: ['css', 'legacy'] },
        };

        html2pdf().set(opt).from(element).save();
    };

    const handleContactClick = () => {
      setShowModal(false);
      setShowContactModal(true);
    };

  return (
    <div className="DocumentGen-bg">
      {hasPaidTrue === false && showModal && (
        <div className="Ai-modal-overlay">
          <div className="paid-modal-content">
            <button className="close-button" onClick={() => setShowModal(false)}>×</button>
             <button className="contact-button" onClick={handleContactClick}>Contact Us To Get Access</button>
          </div>
        </div>
      )}

      {showContactModal && (
        <div className="Ai-modal-overlay">
          <div className="paid-modal-content contact">
            <button className="close-button" onClick={() => setShowContactModal(false)}>×</button>
            <GET_EMAIL />
          </div>
        </div>
      )}
      <div className="DocumentGen-content">
        <div className="DocumentGen-header">
          <h1>AI Policy Compliance Assistant</h1>
        </div>

        {DROPDOWNS.map((dropdown) => (
          <Dropdown key={dropdown.key} title={dropdown.title} disabled={hasPaidTrue === false} >
            <div className="DocumentBox">
              <div className="DocumentBox alt">
                <h2 className="Documenth1">{dropdown.title}</h2>
                <p className="Documenttext">{dropdown.description}</p>
                <br />
                {dropdown.fields.map((question) => (
                  <div key={question} className="DocumentInputGroup">
                    <label className="DocumentLabel">{question}</label>
                    <textarea
                      className="DocumentTextarea"
                      value={answers[dropdown.key]?.[question] || ''}
                      onChange={(e) => {
                        handleChange(dropdown.key, question, e.target.value);
                        e.target.style.height = 'auto';
                        e.target.style.height = `${e.target.scrollHeight}px`;
                      }}
                      rows={1}
                    />
                  </div>
                ))}
                <br />
                <button className="DocumentButton bigger" onClick={() => handleSubmit(dropdown)}>
                  {responseBySection[dropdown.key]?.loading ? 'Advising...' : 'Advise'}
                </button>
                <br />
                {responseBySection[dropdown.key]?.message && (
                  <Dropdown title="Generated Response">                  
                      <pre style={{ whiteSpace: 'pre-wrap' }}>
                        <div id={`pdf-content-${dropdown.key}`} className="GeneratedResponse">
                            <ReactMarkdown remarkPlugins={[remarkGfm]}>{responseBySection[dropdown.key]?.message || ''}</ReactMarkdown>
                        </div>                       
                      </pre>
                      {/*<button className='DocumentButton' onClick={() => handleSavePDF(dropdown.key, dropdown.title)}>Save as PDF</button>*/}
                  </Dropdown>
                )}
              </div>
            </div>
          </Dropdown>
        ))}
      </div>
    </div>
  );
};

export default DocumentGen;


